import { TZDate } from '@date-fns/tz';
import { format } from 'date-fns';

/** The one timezone the whole product schedules in. Centralized for a future per-tenant move. */
export const APP_TIMEZONE = 'Europe/Brussels';

/** Fixed label for time inputs (Belgium runs CET in winter, CEST in summer). */
export const BELGIUM_TZ_LABEL = 'Belgium time (CET/CEST)';

const HALF_HOUR_MS = 30 * 60 * 1000;
const ISO_OFFSET = "yyyy-MM-dd'T'HH:mm:ssXXX";

/**
 * Snap an instant to the nearest :00/:30. Offset-invariant for whole-hour
 * zones like Brussels, so it can operate on the absolute instant.
 */
export function roundToHalfHour(date: Date): Date {
  return new Date(Math.round(date.getTime() / HALF_HOUR_MS) * HALF_HOUR_MS);
}

/**
 * Snap an instant UP to the next :00 or :30 (ceiling). Offset-invariant for
 * whole-hour zones like Brussels. Used to derive the earliest selectable
 * launch time so "now" never lands on an off-grid minute (e.g. 08:39 -> 09:00)
 * and a just-passed slot is never offered.
 */
export function roundUpToHalfHour(date: Date): Date {
  return new Date(Math.ceil(date.getTime() / HALF_HOUR_MS) * HALF_HOUR_MS);
}

/**
 * Interpret a `datetime-local` value ("YYYY-MM-DDTHH:mm") as Brussels
 * wall-clock, snap to the nearest :00 or :30, and emit an ISO-8601 instant
 * with the correct Brussels offset (DST-aware).
 *
 * Non-existent spring-forward wall-clock times (the clocks-jump-ahead gap)
 * are advanced to the next valid time. Ambiguous fall-back times (the
 * repeated hour) resolve to the first (CEST) occurrence.
 *
 * Returns "" for empty or malformed input.
 */
export function wallClockToIso(wallClock: string): string {
  if (!wallClock) return '';
  try {
    const [datePart, timePart = '00:00'] = wallClock.split('T');
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, mi] = timePart.split(':').map(Number);
    const local = new TZDate(y, mo - 1, d, h, mi, 0, APP_TIMEZONE);
    const snapped = new TZDate(roundToHalfHour(local).getTime(), APP_TIMEZONE);
    return format(snapped, ISO_OFFSET);
  } catch {
    return '';
  }
}

/** Render a stored instant as the Brussels `datetime-local` value. */
export function isoToWallClockValue(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return format(new TZDate(d, APP_TIMEZONE), "yyyy-MM-dd'T'HH:mm");
}

/** "YYYY-MM-DD" -> ISO instant at 23:59:59 Brussels. */
export function brusselsEndOfDayIso(dateStr: string): string {
  if (!dateStr) return '';
  const [y, mo, d] = dateStr.split('-').map(Number);
  return format(new TZDate(y, mo - 1, d, 23, 59, 59, APP_TIMEZONE), ISO_OFFSET);
}

/** An absolute instant -> ISO-8601 with the Brussels offset. */
export function dateToBrusselsIso(date: Date): string {
  return format(new TZDate(date, APP_TIMEZONE), ISO_OFFSET);
}

/** The Brussels calendar date ("YYYY-MM-DD") of an instant. */
export function brusselsDateStr(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return format(new TZDate(d, APP_TIMEZONE), 'yyyy-MM-dd');
}

/** Weekday (0=Sun) and hour of an instant, read in Brussels. */
export function brusselsWeekdayHour(date: Date): { weekday: number; hour: number } {
  const b = new TZDate(date, APP_TIMEZONE);
  return { weekday: b.getDay(), hour: b.getHours() };
}

/**
 * The next occurrence of `weekday` at `hour:00` Brussels strictly after `now`,
 * returned as an absolute instant.
 */
export function nextBrusselsOccurrence(now: Date, weekday: number, hour: number): Date {
  const b = new TZDate(now, APP_TIMEZONE);
  const y = b.getFullYear();
  const mo = b.getMonth();
  const d = b.getDate();
  const dayDelta = (weekday - b.getDay() + 7) % 7;
  let cand = new TZDate(y, mo, d + dayDelta, hour, 0, 0, APP_TIMEZONE);
  if (cand.getTime() <= now.getTime()) {
    cand = new TZDate(y, mo, d + dayDelta + 7, hour, 0, 0, APP_TIMEZONE);
  }
  return new Date(cand.getTime());
}
